import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { LanguageInfo, useLanguages } from "../../../context/LanguagesContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import {
  Language,
  Portal,
} from "../../../services/api/types/portalConfiguration/portal";
import {
  brand,
  error as errorPalette,
  gray,
} from "../../../theme/themePrimitives";
import { allLanguages } from "../../../utils/allLanguages";

const LANGUAGE_CHIP_ROW_HEIGHT_PX = 32;
const LANGUAGE_CHIP_ROW_GAP_PX = 6;
const LANGUAGE_ROW_HEIGHT_PX =
  LANGUAGE_CHIP_ROW_HEIGHT_PX + LANGUAGE_CHIP_ROW_GAP_PX;
const MAX_VISIBLE_LANGUAGE_ROWS = 3;

interface LanguagesModalProps {
  languagesModal: boolean;
  setLanguagesModal: React.Dispatch<React.SetStateAction<boolean>>;
  languagesArray: Language[];
  languagesError: string;
  handleLanguagesChange: (_: any, selected: LanguageInfo[]) => void;
  selectedPortal: Portal | null;
  setSelectedPortal: React.Dispatch<React.SetStateAction<Portal | null>>;
  setPortals: React.Dispatch<React.SetStateAction<Portal[]>>;
  handleModalConfirm: () => void;
  modalLoading: boolean;
  isSubPortal?: boolean;
}

export const LanguagesModal: React.FC<LanguagesModalProps> = ({
  handleLanguagesChange,
  languagesArray,
  languagesError,
  languagesModal,
  setLanguagesModal,
  selectedPortal,
  setSelectedPortal,
  setPortals,
  handleModalConfirm,
  modalLoading,
  isSubPortal,
}) => {
  const { languages } = useLanguages();
  const { portals } = usePortal();
  const { showToast } = useToast();

  const handleLanguageChangeWrapper = (_: any, selected: LanguageInfo[]) => {
    if (selectedPortal?.isMain) {
      const removedLanguages = languagesArray.filter(
        (lang) => !selected.some((s) => s.langugageCode === lang.languageCode),
      );

      if (removedLanguages.length > 0) {
        const isUsedElsewhere = removedLanguages.some((removedLang) =>
          portals.portals.some(
            (p) =>
              !p.isMain &&
              p.portalLanguages.some(
                (pl) => pl.languageCode === removedLang.languageCode,
              ),
          ),
        );

        if (isUsedElsewhere) {
          showToast(
            "Some languages can't be removed because they are currently in use by other portals.",
            "error",
          );
          return;
        }
      }
    }
    handleLanguagesChange(_, selected);
  };

  const languagesAutocomplete = selectedPortal?.isMain
    ? allLanguages
    : languages.languages;

  return (
    <>
      <Modal open={languagesModal} onClose={() => setLanguagesModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
            padding: "16px",
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "1rem",
              borderBottom: `1px solid ${gray[300]}`,
            }}
          >
            <Typography variant="h5">Edit Languages</Typography>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={() => setLanguagesModal(false)}
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Stack
            spacing={1}
            py={3}
            sx={{
              borderBottom: `1px solid ${gray[300]}`,
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Typography variant="subtitle2">Language</Typography>
            <Box
              sx={{
                border: `2px solid ${gray[300]}`,
                borderRadius: "4px",
                padding: "4px",
                "&:hover": {
                  borderColor: gray[400],
                },
                "&:focus-within": {
                  borderColor: brand[600],
                },
                ...(languagesError && {
                  borderColor: errorPalette[600],
                }),
              }}
            >
              <Autocomplete
                id="languages-modal-autocomplete"
                disabled={isSubPortal}
                multiple
                options={languagesAutocomplete.filter(
                  (lang) => lang.langugageCode !== "en",
                )}
                value={
                  languagesArray
                    .map((lang) =>
                      languagesAutocomplete.find(
                        (l) => l.langugageCode === lang.languageCode,
                      ),
                    )
                    .filter(Boolean) as LanguageInfo[]
                }
                onChange={handleLanguageChangeWrapper}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.langugageCode === value.langugageCode
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.langugageCode}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <img
                      src={option.flag}
                      alt={option.name}
                      width={24}
                      height={24}
                      style={{
                        border: `1px solid ${gray[300]}`,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    {...params}
                    error={Boolean(languagesError)}
                    helperText={languagesError}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        maxHeight:
                          LANGUAGE_ROW_HEIGHT_PX * MAX_VISIBLE_LANGUAGE_ROWS,
                        overflowY: "auto",
                        alignContent: "flex-start",
                        alignItems: "flex-start",
                        boxSizing: "border-box",
                        scrollSnapType: "y mandatory",
                        rowGap: `${LANGUAGE_CHIP_ROW_GAP_PX}px`,
                        columnGap: "8px",
                        padding: 0,
                        border: "none",
                        "& .MuiOutlinedInput-notchedOutline": {
                          display: "none",
                        },
                      },
                      "& .MuiAutocomplete-input": {
                        height: LANGUAGE_CHIP_ROW_HEIGHT_PX,
                        margin: "0 !important",
                        boxSizing: "border-box",
                        padding: 0,
                        scrollSnapAlign: "start",
                      },
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment
                              position="start"
                              sx={{ marginTop: "4px" }}
                            >
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                renderValue={(value, getTagProps) =>
                  value.map((option: LanguageInfo, index) => {
                    const tagProps = getTagProps({ index });
                    const { key, ...rest } = tagProps;
                    const isEnglish = option.langugageCode === "en";
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        disabled={isSubPortal}
                        label={option.name}
                        avatar={
                          <img
                            src={option.flag}
                            alt={option.name}
                            width={30}
                            height={30}
                            style={{
                              border: `1px solid ${gray[300]}`,
                              borderRadius: "999px",
                              objectFit: "cover",
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                          margin: "0 !important",
                          scrollSnapAlign: "start",
                        }}
                        onDelete={isEnglish ? undefined : rest.onDelete}
                      />
                    );
                  })
                }
                slotProps={{
                  popupIndicator: {
                    sx: { display: "none" },
                  },
                  listbox: {
                    sx: { maxHeight: 260 },
                  },
                  popper: {
                    placement: "top-start",
                  },
                }}
              />
            </Box>
          </Stack>
          {modalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Button
              disabled={isSubPortal}
              onClick={async () => {
                if (selectedPortal) {
                  await handleModalConfirm();
                  setPortals((prevPortals) =>
                    prevPortals.map((p) =>
                      p.portalUid === selectedPortal.portalUid
                        ? {
                            ...p,
                            portalLanguages: languagesArray,
                            subPortals: p.subPortals?.map((sub) => ({
                              ...sub,
                              portalLanguages: languagesArray,
                            })),
                          }
                        : p,
                    ),
                  );
                  setLanguagesModal(false);
                  setSelectedPortal(null);
                }
              }}
              fullWidth
              sx={{
                borderRadius: "12px",
                backgroundColor: isSubPortal
                  ? `${gray[300]} !important`
                  : `${brand[600]} !important`,
                color: "white",
                mt: 2,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                },
                "&.Mui-disabled": {
                  backgroundColor: gray[300],
                  color: "white",
                },
              }}
            >
              <Typography>Save</Typography>
            </Button>
          )}
        </Box>
      </Modal>
    </>
  );
};
